import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TechnologySection = () => {
  const [activeTab, setActiveTab] = useState('ai');

  const tabs = [
    {
      id: 'ai',
      title: 'AI & Machine Learning',
      icon: 'Brain',
      content: {
        title: 'Intelligent Gift Recommendations',
        description: 'Our AI system learns from millions of successful gift-giving experiences to understand what makes gifts meaningful.',
        features: [
          'Relationship context analysis',
          'Personality-based matching',
          'Cultural sensitivity algorithms',
          'Occasion-aware suggestions',
          'Budget optimization',
          'Continuous learning from feedback'
        ],
        ethics: [
          'Transparent recommendation logic',
          'Bias detection and mitigation',
          'Human oversight and control',
          'Explainable AI decisions'
        ]
      }
    },
    {
      id: 'privacy',
      title: 'Privacy & Security',
      icon: 'Shield',
      content: {
        title: 'Your Data, Your Control',
        description: 'We believe privacy is fundamental to trust. Your personal relationships and preferences are protected with enterprise-grade security.',
        features: [
          'End-to-end encryption',
          'Zero-knowledge architecture',
          'Granular privacy controls',
          'Data minimization principles',
          'Regular security audits',
          'GDPR & CCPA compliance'
        ],
        ethics: [
          'No data selling to third parties',
          'Clear consent mechanisms',
          'Right to data deletion',
          'Transparent privacy policies'
        ]
      }
    },
    {
      id: 'platform',
      title: 'Platform Architecture',
      icon: 'Server',
      content: {
        title: 'Built for Scale and Reliability',
        description: 'Our robust infrastructure ensures fast, reliable service that grows with our community while maintaining performance.',
        features: [
          'Cloud-native architecture',
          'Global CDN distribution',
          'Real-time synchronization',
          'Automated scaling',
          '99.9% uptime guarantee',
          'Multi-region redundancy'
        ],
        ethics: [
          'Sustainable cloud practices',
          'Energy-efficient operations',
          'Carbon offset programs',
          'Green technology choices'
        ]
      }
    }
  ];

  const activeContent = tabs.find(tab => tab.id === activeTab)?.content;

  return (
    <section className="py-16 lg:py-24 bg-gradient-warm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl lg:text-5xl font-bold text-accent-800 mb-6">
            Technology & Privacy
          </h2>
          <p className="text-lg lg:text-xl text-accent-600 max-w-3xl mx-auto leading-relaxed">
            Advanced technology built with ethical principles, ensuring personalization without compromising your privacy or autonomy.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-3 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-primary text-white shadow-primary'
                  : 'bg-white text-accent-600 hover:bg-primary-50 hover:text-primary-700 shadow-sm'
              }`}
            >
              <Icon 
                name={tab.icon} 
                size={20} 
                className={activeTab === tab.id ? 'text-white' : 'text-accent-500'}
              />
              <span className="hidden sm:inline">{tab.title}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <h3 className="font-heading text-2xl lg:text-3xl font-bold text-accent-800 mb-6">
                  {activeContent?.title}
                </h3>
                
                <p className="text-lg text-accent-600 mb-8 leading-relaxed">
                  {activeContent?.description}
                </p>
                
                <div className="mb-8">
                  <h4 className="font-heading text-lg font-bold text-accent-800 mb-4">
                    Key Features:
                  </h4>
                  <div className="space-y-3">
                    {activeContent?.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-success-100 rounded-full flex items-center justify-center">
                          <Icon name="Check" size={14} className="text-success-600" />
                        </div>
                        <span className="text-accent-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-gradient-warm rounded-xl p-6 border border-primary-100">
                  <h4 className="font-heading text-lg font-bold text-accent-800 mb-4 flex items-center space-x-2">
                    <Icon name="Heart" size={20} className="text-primary-600" />
                    <span>Ethical Principles</span>
                  </h4>
                  <div className="space-y-3">
                    {activeContent?.ethics.map((principle, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                          <Icon name="Shield" size={14} className="text-primary-600" />
                        </div>
                        <span className="text-accent-600 text-sm">{principle}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Certifications */}
                <div className="mt-8 grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-accent-200 text-center">
                    <Icon name="Award" size={24} className="text-accent-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-accent-800">SOC 2 Type II</p>
                    <p className="text-xs text-accent-600">Certified</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-accent-200 text-center">
                    <Icon name="Shield" size={24} className="text-accent-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-accent-800">GDPR</p>
                    <p className="text-xs text-accent-600">Compliant</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-accent-200 text-center">
                    <Icon name="Lock" size={24} className="text-accent-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-accent-800">ISO 27001</p>
                    <p className="text-xs text-accent-600">Certified</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-accent-200 text-center">
                    <Icon name="Leaf" size={24} className="text-accent-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-accent-800">Carbon</p>
                    <p className="text-xs text-accent-600">Neutral</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Ethics Statement */}
        <div className="mt-12 bg-white rounded-2xl p-8 lg:p-12 shadow-lg border border-accent-100">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Icon name="Brain" size={28} className="text-white" />
            </div>
            <h3 className="font-heading text-2xl lg:text-3xl font-bold text-accent-800 mb-4">
              Our AI Ethics Commitment
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <h4 className="font-heading text-lg font-bold text-accent-800 mb-3">
                Transparency
              </h4>
              <p className="text-accent-600 text-sm">
                You can always understand why we recommend specific gifts and adjust your preferences accordingly.
              </p>
            </div>

            <div className="text-center">
              <h4 className="font-heading text-lg font-bold text-accent-800 mb-3">
                Fairness
              </h4>
              <p className="text-accent-600 text-sm">
                Our algorithms are regularly audited for bias and designed to serve all users equitably.
              </p>
            </div>

            <div className="text-center">
              <h4 className="font-heading text-lg font-bold text-accent-800 mb-3">
                Human Control
              </h4>
              <p className="text-accent-600 text-sm">
                AI enhances human decision-making but never replaces your personal judgment and preferences.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnologySection;